
import React from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Box, Typography, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '../../hooks/useUserRoles';

interface ModuleGuardProps {
    requiredModule: string; // The key of the module (e.g., 'advisor', 'studio')
    requiredSubModule?: string; // Optional: The key of a sub-module (e.g., 'strategic-account-planner')
}

const ModuleGuard: React.FC<ModuleGuardProps> = ({ requiredModule, requiredSubModule }) => {
    const { user, loading: authLoading } = useAuth();
    const { navigationModules, navigationSubModules, isRolesLoading, isSuperAdmin } = useUserRoles();
    const { t } = useTranslation();

    const loading = authLoading || isRolesLoading;

    if (loading) {
        return (
            <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (isSuperAdmin) {
        return <Outlet />;
    }

    // Check main module access using mapped modules
    const hasModuleAccess = navigationModules?.includes(requiredModule);

    // Check sub-module access if specified using mapped sub-modules
    const hasSubModuleAccess = requiredSubModule 
        ? navigationSubModules?.includes(requiredSubModule) 
        : true;

    if (!hasModuleAccess || !hasSubModuleAccess) {
        // Log the denial for debugging
        console.warn(`Access Denied: User ${user?.email} attempted to access module '${requiredModule}' (sub: '${requiredSubModule}') without entitlement.`);
        
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Typography variant="h4" color="error">{t('accessDenied.title', 'Access Denied')}</Typography>
                <Typography>
                    {t('accessDenied.moduleMessage', 'You are not authorized to access this module. Please contact your administrator if you believe this is an error.')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Missing entitlement: {requiredModule} {requiredSubModule ? `/ ${requiredSubModule}` : ''}
                    </Typography>
                </Box>
            </Container>
        );
    }

    return <Outlet />;
};

export default ModuleGuard;
